import React, { useState, useEffect } from 'react';
import { X, CheckCircle } from 'lucide-react';
import Button from '../../../components/common/Button/Button';
import PermissionGate from '../../../components/common/PermissionGate';
import {
    sanitizeTextInput,
    getCharacterCount,
    FIELD_LIMITS,
    NUMBER_LIMITS,
    validateNumberInput
} from '../../../utils/inputValidation';

const ConvertLeadModal = ({ isOpen, onClose, lead, onSubmit }) => {
    const [isConverting, setIsConverting] = useState(false);
    const [formData, setFormData] = useState({
        opportunityName: '',
        value: '',
        description: '',
        expectedCloseDate: ''
    });
    const [formErrors, setFormErrors] = useState({});

    // Helper to get lead name safely
    const getLeadName = (leadObj) => {
        if (!leadObj) return '';
        if (leadObj.firstName && leadObj.lastName) {
            return `${leadObj.firstName} ${leadObj.lastName}`;
        } else if (leadObj.firstName) {
            return leadObj.firstName;
        } else if (leadObj.lastName) {
            return leadObj.lastName;
        } else if (leadObj.name) {
            return leadObj.name;
        } else {
            return 'Unknown Lead';
        }
    };

    useEffect(() => {
        if (isOpen) {
            // Reset form on open and autofill lead name
            setFormData({
                opportunityName: lead ? getLeadName(lead) : '',
                value: '',
                description: '',
                expectedCloseDate: ''
            });
            setFormErrors({});
            setIsConverting(false);
        }
    }, [isOpen, lead]);

    const handleFormChange = (field, value, fieldType = 'text', maxLength = FIELD_LIMITS[field] || 100) => {
        let processedValue = value;

        if (fieldType === 'text') {
            processedValue = sanitizeTextInput(String(value || ''));
            processedValue = processedValue.slice(0, maxLength);
        } else if (fieldType === 'number') {
            // Special handling for opportunity value to enforce upper limit
            if (field === 'value') {
                const maxValue = NUMBER_LIMITS.opportunityValue?.max || 999999999999;
                const numValue = parseFloat(value);

                // If value is valid and exceeds max, clamp it to max
                if (!isNaN(numValue) && numValue > maxValue) {
                    processedValue = maxValue.toString();
                } else {
                    processedValue = value;
                }
            } else {
                const maxValue = NUMBER_LIMITS[field]?.max || Infinity;
                const minValue = NUMBER_LIMITS[field]?.min || 0;
                const numValidation = validateNumberInput(value, maxValue, minValue);
                processedValue = numValidation.value;
            }
        } else if (fieldType === 'textarea') {
            processedValue = sanitizeTextInput(String(value || ''));
            processedValue = processedValue.slice(0, maxLength);
        }

        setFormData(prev => ({
            ...prev,
            [field]: processedValue
        }));

        // Clear error when user starts typing
        if (formErrors[field]) {
            setFormErrors(prev => ({
                ...prev,
                [field]: ''
            }));
        }
    };

    const handleBlur = (field) => {
        let error = '';
        const value = formData[field];

        if (field === 'opportunityName') {
            if (!value || value.trim() === '') {
                error = 'Opportunity name is required';
            }
        } else if (field === 'value') {
            if (value === '' || value === null || value === undefined) {
                error = 'Opportunity value is required';
            } else {
                const numValue = parseFloat(value);
                if (isNaN(numValue) || numValue <= 0) {
                    error = 'Please provide a valid opportunity value greater than 0';
                }
            }
        } else if (field === 'expectedCloseDate') {
            if (!value) {
                error = 'Expected close date is required';
            } else {
                const selectedDate = new Date(value);
                const today = new Date();
                today.setHours(0, 0, 0, 0);
                if (selectedDate < today) {
                    error = 'Expected close date cannot be in the past';
                }
            }
        }

        if (error) {
            setFormErrors(prev => ({
                ...prev,
                [field]: error
            }));
        }
    };

    const validateForm = () => {
        const errors = {};

        // Validate opportunity name
        if (!formData.opportunityName || formData.opportunityName.trim() === '') {
            errors.opportunityName = 'Opportunity name is required';
        }

        // Validate opportunity value
        if (formData.value === '' || formData.value === null || formData.value === undefined) {
            errors.value = 'Opportunity value is required';
        } else {
            const value = parseFloat(formData.value);
            if (isNaN(value) || value <= 0) {
                errors.value = 'Please provide a valid opportunity value greater than 0';
            } else {
                const maxValue = NUMBER_LIMITS.opportunityValue?.max || 999999999999;
                if (value > maxValue) {
                    errors.value = `Opportunity value cannot exceed ${maxValue.toLocaleString()}`;
                }
            }
        }

        // Validate expected close date
        if (!formData.expectedCloseDate) {
            errors.expectedCloseDate = 'Expected close date is required';
        } else {
            const selectedDate = new Date(formData.expectedCloseDate);
            const today = new Date();
            today.setHours(0, 0, 0, 0);

            if (selectedDate < today) {
                errors.expectedCloseDate = 'Expected close date cannot be in the past';
            }
        }

        setFormErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const handleSubmit = async () => {
        if (!lead) return;

        if (!validateForm()) {
            return;
        }

        setIsConverting(true);

        try {
            // Prepare opportunity data
            const opportunityData = {
                name: formData.opportunityName.trim(),
                amount: parseFloat(formData.value),
                description: formData.description || undefined,
                expectedCloseDate: formData.expectedCloseDate
                    ? new Date(formData.expectedCloseDate).toISOString()
                    : undefined
            };

            // Remove undefined values
            Object.keys(opportunityData).forEach(key => {
                if (opportunityData[key] === undefined || opportunityData[key] === null || opportunityData[key] === '') {
                    delete opportunityData[key];
                }
            });

            await onSubmit(lead, opportunityData);
            // Note: We don't close the modal here automatically unless the parent updates isOpen prop.
            // Usually the parent will handle success by closing the modal.
        } catch (error) {
            // Parent should handle error notifications
        } finally {
            setIsConverting(false);
        }
    };


    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] !mt-0 p-4">
            <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
                <div className="p-6">
                    <div className="flex items-center justify-between mb-6">
                        <h3 className="text-lg font-semibold text-gray-900">Convert Lead to Opportunity</h3>
                        <button
                            onClick={onClose}
                            className="text-gray-400 hover:text-gray-600"
                        >
                            <X className="h-6 w-6" />
                        </button>
                    </div>

                    {/* Lead Information */}
                    {lead ? (
                        <div className="bg-gray-50 rounded-lg p-4 mb-6">
                            <h4 className="text-sm font-medium text-gray-900 mb-3">Lead Information</h4>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <p className="text-sm text-gray-600">Name</p>
                                    <p className="text-sm font-medium text-gray-900 break-words capitalize" title={getLeadName(lead)}>{getLeadName(lead)}</p>
                                </div>
                                <div>
                                    <p className="text-sm text-gray-600">Company</p>
                                    <p className="text-sm font-medium text-gray-900 break-words capitalize" title={lead.company}>{lead.company || 'N/A'}</p>
                                </div>
                                <div>
                                    <p className="text-sm text-gray-600">Email</p>
                                    <p className="text-sm font-medium text-gray-900 break-words" title={lead.email}>{lead.email || 'N/A'}</p>
                                </div>
                                <div>
                                    <p className="text-sm text-gray-600">Phone</p>
                                    <p className="text-sm font-medium text-gray-900 break-words" title={lead.phone}>{lead.phone || 'N/A'}</p>
                                </div>
                            </div>
                        </div>
                    ) : (
                        <div className="bg-red-50 rounded-lg p-4 mb-6">
                            <p className="text-sm text-red-600 break-words">No lead selected. Please close this modal and try again.</p>
                        </div>
                    )}

                    {/* Opportunity Details Form */}
                    <div className="space-y-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Opportunity Name <span className="text-red-500 ml-1">*</span>
                            </label>
                            <input
                                type="text"
                                value={formData.opportunityName}
                                onChange={(e) => handleFormChange('opportunityName', e.target.value, 'text', FIELD_LIMITS.opportunityName)}
                                onBlur={() => handleBlur('opportunityName')}
                                className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-agrosaf-green focus:border-transparent ${formErrors.opportunityName ? 'border-red-300 focus:ring-red-500' : 'border-gray-300'
                                    }`}
                                style={{ wordBreak: 'break-word', overflowWrap: 'break-word' }}
                                placeholder="Enter opportunity name"
                                maxLength={FIELD_LIMITS.opportunityName}
                            />
                            <div className="flex justify-between mt-1">
                                {formErrors.opportunityName && (
                                    <p className="text-sm text-red-600">{formErrors.opportunityName}</p>
                                )}
                                <p className="text-xs text-gray-500 ml-auto">
                                    {getCharacterCount(formData.opportunityName, FIELD_LIMITS.opportunityName)}
                                </p>
                            </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Opportunity Value <span className="text-red-500 ml-1">*</span>
                                </label>
                                <input
                                    type="number"
                                    value={formData.value}
                                    onChange={(e) => handleFormChange('value', e.target.value, 'number', Infinity)}
                                    onBlur={() => handleBlur('value')}
                                    min="0"
                                    max={NUMBER_LIMITS.opportunityValue?.max || 999999999999}
                                    className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-agrosaf-green focus:border-transparent ${formErrors.value ? 'border-red-300 focus:ring-red-500' : 'border-gray-300'
                                        }`}
                                    placeholder="Enter opportunity value"
                                />
                                {formErrors.value && (
                                    <p className="mt-1 text-sm text-red-600">{formErrors.value}</p>
                                )}
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Expected Close Date <span className="text-red-500 ml-1">*</span>
                                </label>
                                <input
                                    type="date"
                                    value={formData.expectedCloseDate}
                                    onChange={(e) => handleFormChange('expectedCloseDate', e.target.value, 'text')}
                                    onClick={(e) => e.target.showPicker?.()}
                                    onBlur={() => handleBlur('expectedCloseDate')}
                                    min={new Date().toISOString().split('T')[0]}
                                    className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-agrosaf-green focus:border-transparent ${formErrors.expectedCloseDate ? 'border-red-300 focus:ring-red-500' : 'border-gray-300'
                                        }`}
                                />
                                {formErrors.expectedCloseDate && (
                                    <p className="mt-1 text-sm text-red-600">{formErrors.expectedCloseDate}</p>
                                )}
                            </div>
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Description
                            </label>
                            <textarea
                                value={formData.description}
                                onChange={(e) => handleFormChange('description', e.target.value, 'textarea', FIELD_LIMITS.opportunityDescription)}
                                rows={3}
                                className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-agrosaf-green focus:border-transparent break-words ${formErrors.description ? 'border-red-300 focus:ring-red-500' : 'border-gray-300'
                                    }`}
                                style={{ wordBreak: 'break-word', overflowWrap: 'break-word' }}
                                placeholder="Enter opportunity description"
                                maxLength={FIELD_LIMITS.opportunityDescription}
                            />
                            <div className="flex justify-between mt-1">
                                {formErrors.description && (
                                    <p className="text-sm text-red-600">{formErrors.description}</p>
                                )}
                                <p className="text-xs text-gray-500 ml-auto">
                                    {getCharacterCount(formData.description, FIELD_LIMITS.opportunityDescription)}
                                </p>
                            </div>
                        </div>

                    </div>

                    {/* Modal Actions */}
                    <div className="flex items-center justify-end space-x-3 mt-6 pt-6 border-t border-gray-200">
                        <Button
                            variant="outline"
                            onClick={onClose}
                            disabled={isConverting}
                        >
                            Cancel
                        </Button>
                        <PermissionGate permission="lead_management" action={["create", "update"]}>
                            <span title={isConverting ? "Conversion in progress..." : (!lead ? "No lead selected" : "")}>
                                <Button
                                    variant="primary"
                                    icon={isConverting ? <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div> : <CheckCircle className="h-4 w-4" />}
                                    onClick={handleSubmit}
                                    disabled={isConverting || !lead}
                                    className="bg-agrosaf-green hover:bg-agrosaf-darkGreen"
                                >
                                    {isConverting ? 'Converting...' : 'Convert to Opportunity'}
                                </Button>
                            </span>
                        </PermissionGate>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ConvertLeadModal;
